import { atom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';

export const TOTAL_COACH_MARKS_WITH_RECORDING = 5;
export const TOTAL_COACH_MARKS_SKIP_TO_RESULTS = 3;

const createSessionStorageAdapter = <T>() =>
  typeof globalThis.window === 'undefined'
    ? undefined
    : createJSONStorage<T>(() => globalThis.sessionStorage);

const RECORDING_COACH_MARK_IDS: Record<number, string> = {
  0: 'refine_note',
  1: 'create_docs',
  2: 'clinical_suggestions',
  3: 'control_sources',
  4: 'new_session',
};

const SKIP_COACH_MARK_IDS: Record<number, string> = {
  0: 'refine_note',
  1: 'create_docs',
  2: 'new_session',
};

export function getCoachMarkId(
  step: number,
  didSkipToResults: boolean
): string | undefined {
  return didSkipToResults ? SKIP_COACH_MARK_IDS[step] : RECORDING_COACH_MARK_IDS[step];
}

export const isDemoInProgressAtom = atom(false);

export const demoCoachMarkStepAtom = atomWithStorage<number | null>(
  'demo-coach-mark-step',
  null,
  createSessionStorageAdapter<number | null>(),
  { getOnInit: true }
);

/** True when user chose "Skip to results" instead of recording */
export const didSkipToResultsAtom = atomWithStorage<boolean>(
  'demo-did-skip-to-results',
  false,
  createSessionStorageAdapter<boolean>(),
  { getOnInit: true }
);

/**
 * SCR-7237: latched true when the tour ends (completed or dismissed) so it
 * cannot re-enqueue within the same browsing session, even if a stale
 * `GET /user` response briefly reverts `is_demo_on_desktop_completed` in the
 * query cache. Session-scoped on purpose: across sessions the backend flag is
 * the source of truth.
 */
export const demoCoachMarksEndedAtom = atomWithStorage<boolean>(
  'heidi.demoFlow.coachMarksEnded',
  false,
  createSessionStorageAdapter<boolean>(),
  { getOnInit: true }
);

/** One-shot signal from ScribeOnboardingModal to auto-start the demo */
export const autoStartDemoAtom = atom<boolean>(false);

/**
 * GRO-3186: the `session_id` of the current shortened-demo session, or `null`
 * when the user is not in a demo. Set when the user clicks "Try a demo" and
 * read by `analyticsScribeModel` to tag `transcription_*` and `note_*` events
 * with `demo_mode: 'shortened'` only when they belong to this session.
 *
 * Persisted via sessionStorage so events firing after a reload still tag
 * correctly. We never explicitly reset it — the sessionId comparison handles
 * cleanup naturally: events for any *other* session don't match and aren't
 * tagged, even though the atom still holds the original demo sessionId.
 */
export const currentDemoSessionIdAtom = atomWithStorage<string | null>(
  'demo-current-session-id',
  null,
  createSessionStorageAdapter<string | null>(),
  { getOnInit: true }
);
